import AntiforgeryService from '../../../../../services/anti-forgery.service';
import { SignFault, SignFaultHelper } from '../../../../../models/sign-fault.enum';
import type { IGrp3CollectResponseApiModel, IGrp3SignResponseApiModel } from '../models/grp3-sign.api-model';

export default class Grp3SignService {
  static async collect(transactionId: string, signal?: AbortSignal): Promise<IGrp3CollectResponseApiModel> {
    const response = await this.post('/api/digitalidentity/grp3/collect', { transactionId }, signal, undefined, true);
    return (await response.json()) as IGrp3CollectResponseApiModel;
  }

  static async getQrImage(transactionId: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.post('/api/digitalidentity/grp3/qr', { transactionId }, signal);
    return response.blob();
  }

  static async cancel(transactionId: string): Promise<void> {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        await this.post('/api/digitalidentity/grp3/cancel', { transactionId }, undefined, true);
        return;
      } catch {
        if (attempt === 1) throw new Error('Unable to cancel BankID signing');
      }
    }
  }

  static async renew(transactionId: string, contentGuid: string, personalIdentityNumber: string, language: string): Promise<IGrp3SignResponseApiModel> {
    const response = await this.post(`/api/digitalidentity/grp3/renew?language=${encodeURIComponent(language)}`, { transactionId, contentGuid, personalIdentityNumber });
    return (await response.json()) as IGrp3SignResponseApiModel;
  }

  private static async post(url: string, body: object, signal?: AbortSignal, keepalive?: boolean, parseFault = false): Promise<Response> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': await AntiforgeryService.getToken(),
      },
      body: JSON.stringify(body),
      signal,
      keepalive,
    });
    if (!response.ok) {
      if (parseFault) {
        let fault: unknown;
        try {
          fault = JSON.parse(await response.text());
        } catch {
          fault = undefined;
        }
        if (typeof fault === 'string' && SignFaultHelper.parse(fault) !== SignFault.unknown && fault !== SignFault.none) throw new Error(fault);
      }
      throw new Error(`BankID request failed with status ${response.status}`);
    }
    return response;
  }
}
